
import { defineComponent, computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { QuestionForm } from "../../../../../domain/entities/search-form/question_form";
import { dispatchError } from "../../../utils/error_handling";
import { reloadContent } from "../../../views/content/questionnaire_error_handler";

export default defineComponent({
  name: "QuestionFormNavigation",
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    questionForm: {
      type: Object as () => QuestionForm,
      required: true
    },
    questionType: {
      type: Number,
      required: true
    },
    stars: Number,
    text: String,
    single: Number,
    multi: Array as () => number[]
  },
  emits: ["goNext", "goPrevious"],
  setup(props, { emit }) {
    const route = useRoute();
    const store = useStore();

    const hasNext = computed(() => props.currentPage < props.totalPages);
    const hasPrevious = computed(() => props.currentPage > 1);

    const alreadyAnswered = computed(() =>
      store.state.question_form_module.answered.has(props.questionForm.id)
    );

    const isCurrentValid = computed(() => {
      switch (props.questionType) {
        case 1:
          return !!props.stars;
        case 2:
          return !!props.single;
        case 3:
          return !!props.multi && props.multi.length > 0;
        case 4:
          return !!props.text && !!props.text.trim();
        default:
          return false;
      }
    });

    const answeredCount = computed(
      () => store.state.question_form_module.answered.size
    );

    const canFinish = computed(() => {
      const extra = alreadyAnswered.value ? 0 : isCurrentValid.value ? 1 : 0;
      return answeredCount.value + extra >= props.totalPages;
    });

    const handleSendAnswer = async (): Promise<boolean> => {
      const searchFormID = Number(route.params.content_id);
      const trainingID = Number(route.params.course_id);
      const questionID = props.questionForm.id;

      switch (props.questionType) {
        case 1:
          if (!props.stars) return false;
          props.questionForm.stars = props.stars;
          break;
        case 2:
          if (!props.single) return false;
          props.questionForm.answerId = props.single;
          break;
        case 3:
          if (!props.multi || props.multi.length === 0) return false;
          props.questionForm.multiAnswerIds = props.multi;
          break;
        case 4:
          if (!props.text || !props.text.trim()) return false;
          props.questionForm.answerText = props.text.trim();
          break;
        default:
          return false;
      }

      await store.dispatch("question_form_module/responseQuestions", {
        searchFormID,
        trainingID,
        questionID,
        questionForm: props.questionForm
      });

      return true;
    };

    const onFinishForm = async () => {
      const answered = await handleSendAnswer();
      if (!answered) return;
      try {
        const searchFormID = Number(route.params.content_id);
        await store.dispatch("content_module/setCurrentContent", searchFormID);
        await store.dispatch("content_module/finishContent");
        await reloadContent();
      } catch (e) {
        await dispatchError(e);
      }
    };

    const onNext = async () => {
      await handleSendAnswer();
      const searchFormID = Number(route.params.content_id);
      await store.dispatch("content_module/setCurrentContent", searchFormID);
      emit("goNext");
    };

    const onPrevious = async () => {
      await handleSendAnswer();
      const searchFormID = Number(route.params.content_id);
      await store.dispatch("content_module/setCurrentContent", searchFormID);
      emit("goPrevious");
    };

    return {
      hasNext,
      hasPrevious,
      canFinish,
      onFinishForm,
      onNext,
      onPrevious
    };
  }
});
